import { useCallback, useEffect, useMemo, useState } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { getCoreRowModel } from '@tanstack/react-table';

// MUI Theme
import { Button, Paper } from '@mui/material';
import { LanguageOutlined } from '@mui/icons-material';
import { Theme } from '@mui/material/styles';
import { makeStyles } from '@mui/styles';

// Services
import {
  addOrUpdateLanguage,
  deleteLanguage,
  deleteLanguageLabel,
  getAvailableLanguages,
  getPreferredLanguage,
  updateLanguageDisplayName,
} from '../../../services/language.services';

// Utils
import { Storage } from '../../../utils/Storage';
import useConfirmDialog from '../../../hooks/useConfirmDialog';
import { useNotifyMessage } from '../../../hooks/useNotifyMessage';
import { makeLanguageColumn } from '../../../utils/columns/makelanguageColumn';

// Types
import { CONFIRM_DIALOG_CANCEL, CONFIRM_LANGUAGE_LABEL_DELETE, Language, SearchBarFilterTypes } from '../../../constants/Constants';
import { AppPermissions } from '../../../constants/Permissions';
import { TranslationItem } from '../../../types/Language';

// Common Components
import { useStandardTable as useTable, Table } from '../../common/table/index';
import { AuthGuard } from '../../common/AuthGuard';
import CustomModal from '../../common/withModal';

// Components
import LanguageDisplayName from './components/LanguageDisplayName';
import UpdateLanguage from './components/UpdateLanguage';
import AddLanguage from './components/AddLanguage';
import BulkUpdateLanguage from './components/BulkUpdateLanguage';
import { tablePrimaryButtonSx } from '../../../styles/tableButtonStyles';

const useStyles = makeStyles<Theme>((theme) => ({
  heading: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '0 10px',
    borderBottom: '1px solid #ccc',
    [theme.breakpoints.up('lg')]: {
      height: '60px',
    },
  },
  syncIcon: {
    fontSize: 20,
  },
}));

const Languages = () => {
  const [isEditing, setIsEditing] = useState<{ isEdit: boolean; label: string }>({
    isEdit: false,
    label: '',
  });
  const [changeDisplayName, setChangeDisplayName] = useState<TranslationItem | null>(null);
  const [paramFilter, setParamFilter] = useState<any>({});
  const [hasFilterChanged, setHasFilterChanged] = useState<boolean>(false);
  const [preferredLanguage, setPreferredLanguage] = useState<any>([]);
  const [refreshUpdateLanguage, setRefreshUpdateLanguage] = useState(false);
  const [isAddLangDialogOpen, setIsAddLangDialogOpen] = useState<boolean>(false);
  const [isBulkUpdateDialogOpen, setIsBulkUpdateDialogOpen] = useState<boolean>(false);
  const [isEditLangDialogOpen, setIsEditLangDialogOpen] = useState<{ open: boolean; isoCode: string | null }>({
    open: false,
    isoCode: null,
  });
  const { notifyMessage } = useNotifyMessage();
  const { renderDialog, confirm } = useConfirmDialog(
    isEditLangDialogOpen.isoCode === preferredLanguage.isoCode ? CONFIRM_LANGUAGE_LABEL_DELETE : CONFIRM_DIALOG_CANCEL,
  );

  const classes = useStyles();

  const forceReload = useCallback(() => setParamFilter((prev) => ({ ...prev, changedAt: new Date() })), []);

  const defaultFilters = useCallback(() => {
    const filterState = Storage.getSearchBarFiltering(SearchBarFilterTypes.LANGUAGE);
    if (filterState) {
      setParamFilter({
        filters: filterState.filters,
        sortBy: filterState.sortBy,
      });
    }
  }, []);

  const fetchPrefferedLanguage = async () => {
    const response: any = await getPreferredLanguage();
    if (response.status === 200) {
      setPreferredLanguage(response.data);
    }
  };

  useEffect(() => {
    fetchPrefferedLanguage();
  }, []);

  useEffect(() => {
    defaultFilters();
    if (hasFilterChanged) {
      forceReload();
    }
  }, [hasFilterChanged, defaultFilters, forceReload]);

  const formik = useFormik<TranslationItem>({
    initialValues: {
      isoCode: '',
      key: '',
      value: '',
    },
    validationSchema: Yup.object<TranslationItem>({
      isoCode: Yup.string().required(Language.feildRequiredLabel),
      key: Yup.string().required(Language.feildRequiredLabel),
      value: Yup.string().required(Language.feildRequiredLabel),
    }),
    enableReinitialize: true,
    validateOnBlur: true,
    onSubmit: async (values) => {
      const payload: TranslationItem = { ...values, value: values.value ? values.value.replace(/\\n/g, '\n') : values.value };
      try {
        const res: any = await addOrUpdateLanguage(payload);
        if (res.status === 200) {
          notifyMessage(Language.updateLanguageSuccess, 'success');
          setIsAddLangDialogOpen(false);
          forceReload();
          setRefreshUpdateLanguage((prev) => !prev);
        } else {
          notifyMessage(res.data);
        }
      } catch (error) {
        notifyMessage(Language.updateLanguageFailed, 'error');
        console.error('error', error);
      }
    },
  });

  const handleEdit = useCallback(
    (languageLabel: TranslationItem) => {
      formik.setValues(languageLabel);
      setIsEditLangDialogOpen({ open: true, isoCode: languageLabel.isoCode });
    },
    [formik],
  );

  const handleCloseEditDialog = useCallback(() => {
    setIsEditLangDialogOpen({ open: false, isoCode: null });
    setIsEditing({ isEdit: false, label: '' });
  }, []);

  const handleDelete = useCallback(
    async (isoCode: string) => {
      const { accepted } = await confirm({});
      if (!accepted) return;
      try {
        await deleteLanguage(isoCode);
        notifyMessage(Language.removeLanguageSuccess, 'success');
        forceReload();
      } catch (err) {
        console.error(Language.removeLanguageFailed, err);
        notifyMessage(Language.removeLanguageFailed, 'error');
      }
    },
    [confirm, notifyMessage, forceReload],
  );

  const handleEditLabel = useCallback(
    (language: any) => {
      formik.setValues({
        isoCode: isEditLangDialogOpen.isoCode,
        key: language.name,
        value: language.value,
      });
      setIsEditing({ isEdit: true, label: language.label });
      setIsAddLangDialogOpen(true);
    },
    [formik, isEditLangDialogOpen.isoCode],
  );

  const handleDeleteLabel = useCallback(
    async (isoCode: string, label: string) => {
      const { accepted } = await confirm({});
      if (!accepted) return;
      try {
        await deleteLanguageLabel(isoCode, label);
        notifyMessage(Language.labelRemoved, 'success');
        setRefreshUpdateLanguage((prev) => !prev);
      } catch (err) {
        console.error(Language.labelRemovalFailed, err);
        notifyMessage(Language.labelRemovalFailed, 'error');
      }
    },
    [confirm, notifyMessage, forceReload],
  );

  const handleOpenDisplayNameModal = useCallback((language: TranslationItem) => {
    setChangeDisplayName(language);
  }, []);

  const handleUpdateDisplayName = useCallback(
    async (displayName: string, isoCode: string) => {
      try {
        await updateLanguageDisplayName(displayName, isoCode);
        setChangeDisplayName(null);
        notifyMessage(Language.updateLanguageDisplayNameSuccess, 'success');
        setHasFilterChanged(true);
      } catch (error) {
        console.error(`${Language.updateLanguageDisplayNameSuccess} ${isoCode}:`, error);
      }
    },
    [notifyMessage],
  );

  const columns = useMemo(
    () => makeLanguageColumn(handleOpenDisplayNameModal, handleDelete, handleEdit),
    [handleOpenDisplayNameModal, handleDelete, handleEdit],
  );

  const table = useTable<any>({
    columns,
    fetchAsync: getAvailableLanguages,
    params: paramFilter,
    serverSide: true,
  });

  return (
    <AuthGuard permission={AppPermissions.Label.Edit}>
      {renderDialog()}
      <div className={classes.heading}>
        <h4>{Language.heading}</h4>
        <Button
          onClick={() => {
            formik.resetForm();
            setIsAddLangDialogOpen(true);
          }}
          variant='contained'
          size='small'
          endIcon={<LanguageOutlined className={classes.syncIcon} />}
          sx={tablePrimaryButtonSx}
        >
          {Language.addLanguage}
        </Button>
      </div>
      <CustomModal
        title={Language.updateLanguage}
        isOpen={isEditLangDialogOpen.open}
        isValid={formik.isValid}
        setIsOpen={handleCloseEditDialog}
        onConfirm={formik.handleSubmit}
        maxWidth='lg'
        showSaveButton={false}
      >
        <Button
          onClick={() => setIsBulkUpdateDialogOpen(true)}
          variant='contained'
          size='small'
          style={{ textTransform: 'none', position: 'absolute', top: 15, right: 15 }}
          endIcon={<LanguageOutlined className={classes.syncIcon} />}
        >
          {Language.bulkUpdate}
        </Button>
        <UpdateLanguage formRef={formik} handleDelete={handleDeleteLabel} handleEdit={handleEditLabel} refresh={refreshUpdateLanguage} />
      </CustomModal>

      <CustomModal
        title={Language.bulkUpdate}
        isOpen={isBulkUpdateDialogOpen}
        setIsOpen={setIsBulkUpdateDialogOpen}
        onConfirm={() => {
          setIsBulkUpdateDialogOpen(false);
        }}
        maxWidth='lg'
        showSaveButton={false}
      >
        <BulkUpdateLanguage
          isoCode={isEditLangDialogOpen.isoCode}
          onBulkUploadComplete={() => {
            setIsBulkUpdateDialogOpen(false); // Close the modal
            setRefreshUpdateLanguage((prev) => !prev); // Trigger refresh
          }}
        />
      </CustomModal>

      <CustomModal
        title={isEditing.isEdit ? Language.updateLanguage : Language.addLanguage}
        isOpen={isAddLangDialogOpen}
        isValid={formik.isValid}
        setIsOpen={setIsAddLangDialogOpen}
        onConfirm={formik.handleSubmit}
        maxWidth='xs'
      >
        <AddLanguage formRef={formik} isoCode={isEditLangDialogOpen.isoCode} isEditing={isEditing.isEdit} />
      </CustomModal>

      <CustomModal
        title={Language.updateDisplayNameLabel}
        isOpen={!!changeDisplayName}
        setIsOpen={() => setChangeDisplayName(null)}
        maxWidth='sm'
        showSaveButton={false}
      >
        {changeDisplayName && <LanguageDisplayName language={changeDisplayName} onSubmit={handleUpdateDisplayName} />}
      </CustomModal>

      <Paper>
        <Table {...table} showSearchBar={false} showColumnToggler={false} getCoreRowModel={getCoreRowModel()} />
      </Paper>
    </AuthGuard>
  );
};

export default Languages;
